import { createApp } from 'vue'
import { createPinia, setActivePinia } from 'pinia'
import router from "@/router/index"
import type { LocationQueryRaw } from 'vue-router'
import App from './App.vue'
import { Constant } from './config/constants'
import axios, { type AxiosError } from 'axios';
import { useAuthStore } from '@/stores/authStore'

// Vuetify
import "vuetify/styles"
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'


// Create Vuetify instance with correct initial theme
const vuetify = createVuetify({
    components,
    directives,
    theme: {
        defaultTheme: 'light',
        themes: {
            light: {
                dark: false,
                colors: {
                    primary: '#eab308', // Yellow for duck theme
                    secondary: '#334155',
                    accent: '#82B1FF',
                    error: '#FF5252',
                    info: '#2196F3',
                    success: '#4CAF50',
                    warning: '#FB8C00',
                }
            }
        },
    },
})

// Import styles after vuetify for overriding
import './style.css'
import './plugins/axios'

// Create pinia store
const pinia = createPinia();
setActivePinia(pinia);

const AUTH_IGNORED_PATHS = [
  Constant.LOGIN,
  Constant.REGISTER,
  Constant.CONFIRM_EMAIL,
  Constant.FORGOT_PASSWORD,
  Constant.RESET_PASSWORD
];

let lastAuthRedirectAt = 0;
const AUTH_REDIRECT_COOLDOWN_MS = 1000;
const AUTH_PROMPT_MESSAGE = 'Your session has expired. Please log in again to keep exploring the duck hunt!';

// Set up axios interceptors for auth handling
axios.interceptors.response.use(
  response => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const requestUrl: string = error.config?.url ?? '';
  const headers = error.config?.headers as Record<string, unknown> | undefined;
  const hasAuthHeader = Boolean(headers?.Authorization || headers?.authorization);
  const storedToken = localStorage.getItem('token');
    const isIgnored = AUTH_IGNORED_PATHS.some(path => requestUrl.includes(path));
  const isNetworkAuthFailure = !error.response && error.code === 'ERR_NETWORK' && !isIgnored && (!!storedToken || hasAuthHeader);

    if ((status === 401 || status === 403 || isNetworkAuthFailure) && !isIgnored) {
      const now = Date.now();
      if (now - lastAuthRedirectAt > AUTH_REDIRECT_COOLDOWN_MS) {
        lastAuthRedirectAt = now;

        const authStore = useAuthStore();
        authStore.logout();

        const currentRoute = router.currentRoute.value;
        const alreadyShowingLogin = currentRoute.query?.showLogin === 'true';

        if (!alreadyShowingLogin) {
          const nextQuery = { ...currentRoute.query } as LocationQueryRaw;
          nextQuery.showLogin = 'true';

          if (!nextQuery.message) {
            nextQuery.message = AUTH_PROMPT_MESSAGE;
          }

          if (!nextQuery.redirect) {
            nextQuery.redirect = currentRoute.fullPath;
          }

          router.replace({ path: currentRoute.path, query: nextQuery }).catch(() => undefined);
        }
      }
    }

    return Promise.reject(error);
  }
);

// Create and mount app
const app = createApp(App)
  .use(router)
  .use(pinia)
  .use(vuetify);

// Mount the app
app.mount('#app');

// Export vuetify instance for potential use elsewhere
export { vuetify };
